import { isEmpty } from '@sgme/fp';
import { parseISO } from 'date-fns';
import { mapValues } from 'lodash-es';
import type { ReloadedTileData } from 'state/quotes/dashboard/reloadedTiles/reloadedTiles.model';
import type { GroupColor, TileMetadata } from 'state/quotes/dashboard/tiles/tiles.model';
import type { Collection } from 'state/utils/types';
import { now } from 'utils/core/now';
import type { IsoDateOnly } from 'utils/date';
import { getDateOnlyFromDate } from 'utils/date';
import { isNonEmptyAfterTrim } from 'utils/string';

import type { PersistedDashboard, PersistedData, PersistedMetadata, PersistencePayload } from './model';

function sanitizeTileDataPayload(tile: PersistedData) {
  const clientId = isNonEmptyAfterTrim(tile.clientId) ? tile.clientId : undefined;
  const clientName = isNonEmptyAfterTrim(tile.clientName) ? tile.clientName : undefined;
  const lastUpdated = tile.lastUpdated ? parseISO(tile.lastUpdated) : now();
  return {
    ...tile,
    clientId,
    clientName,
    lastUpdated,
  } as ReloadedTileData;
}

function sanitizeTileDataRequest(tile: ReloadedTileData) {
  return {
    ...tile,
    lastUpdated: tile.lastUpdated.toISOString(),
  } as PersistedData;
}

function sanitizeTileMetadataPayload(tile: PersistedMetadata) {
  return {
    isPinned: tile.isPinned,
    groupName: tile.groupName,
    groupColor: sanitizeGroupColor(tile.groupColor),
  } as TileMetadata;
}

export function sanitizeResult({ quoteIds, tiles, metadata }: PersistedDashboard): PersistencePayload {
  return {
    quoteIds,
    metadata: mapValues(metadata, sanitizeTileMetadataPayload),
    data: mapValues(tiles, sanitizeTileDataPayload),
  };
}

export function getPreviousDayOfActivity(
  today: IsoDateOnly,
  tilesLastUpdate: readonly IsoDateOnly[],
): Date | undefined {
  const mostRecentExceptToday = tilesLastUpdate.reduce((mostRecent, date) => {
    if (mostRecent < date && date !== today) {
      return date;
    }
    return mostRecent;
  }, '');
  if (!isEmpty(mostRecentExceptToday)) {
    return parseISO(mostRecentExceptToday);
  }
}

function _isTilePinned(meta: TileMetadata): boolean {
  return meta.isPinned === true || meta.groupName !== undefined || meta.groupColor !== undefined;
}
export function findQuoteTooOld<T extends { lastUpdated: Date }>(
  data: Collection<T>,
  metadata: Collection<TileMetadata>,
  today: IsoDateOnly,
): string[] {
  const cutoffDate = getPreviousDayOfActivity(
    today,
    Object.values(data).map(t => getDateOnlyFromDate(t.lastUpdated)!),
  );
  if (cutoffDate === undefined) {
    return [];
  }
  return Object.entries(data)
    .filter(([quoteId, { lastUpdated }]) => !_isTilePinned(metadata[quoteId]) && lastUpdated < cutoffDate)
    .map(([quoteId, _]) => quoteId);
}

export function sanitizeRequest({ data, metadata, quoteIds }: PersistencePayload): PersistedDashboard {
  return {
    quoteIds,
    metadata,
    tiles: mapValues(data, sanitizeTileDataRequest),
  };
}

const legacyColors: string[] = ['gray', 'blue', 'red', 'orange', 'green', 'pink', 'purple', 'cyan'];

const closestGroupColor: GroupColor[] = [5, 2, 3, 0, 1, 6, 7, 4];

export function sanitizeGroupColor(value: GroupColor | string | undefined): GroupColor | undefined {
  const parsed = Number.parseInt(value as string, 10);
  if (!Number.isNaN(parsed) && parsed <= 7 && parsed >= 0) {
    return parsed as GroupColor;
  }
  const colorIndex = legacyColors.indexOf(value as string);
  if (colorIndex !== -1) {
    return closestGroupColor[colorIndex];
  }
  return undefined;
}
